<template>
    <div
        v-if="isVisible"
        class="information-bar"
        :class="{ 'information-bar--blocking': isBlocking }"
        role="status"
    >
        <div
            class="information-bar__bar l-inline l-center-v"
            :class="`information-bar__bar--${normalizedSeverity}`"
        >
            <span class="information-bar__content l-inline l-center-v">
                <component
                    :is="severityIcon"
                    class="information-bar__icon"
                    width="20"
                    height="20"
                    color="currentColor"
                />

                <span v-if="label" class="information-bar__label">{{
                    label
                }}</span>

                <span class="information-bar__message">{{
                    localizedMessage
                }}</span>
            </span>
        </div>
    </div>
</template>

<script>
import AlarmBellIcon from '@/components/icons/AlarmBellIcon'
import InfoIcon from '@/components/icons/InfoIcon'
import WarningIcon from '@/components/icons/WarningIcon'

const DEFAULT_POLL_INTERVAL = 5 * 60 * 1000
const MIN_POLL_INTERVAL = 30 * 1000
const SEVERITIES = ['info', 'announcement', 'downtime']

export default {
    name: 'InformationBar',
    components: {
        AlarmBellIcon,
        InfoIcon,
        WarningIcon,
    },
    data() {
        return {
            config: null,
            pollTimer: null,
        }
    },
    computed: {
        isVisible() {
            return Boolean(
                this.config &&
                    this.config.enabled !== false &&
                    this.localizedMessage
            )
        },
        normalizedSeverity() {
            const severity = this.config?.severity

            return SEVERITIES.includes(severity) ? severity : 'info'
        },
        localizedMessage() {
            const message = this.config?.message

            if (!message) {
                return ''
            }

            if (typeof message === 'string') {
                return message
            }

            return (
                message[this.$i18n.locale] ||
                message[this.$i18n.locale?.split('-')[0]] ||
                message.en ||
                Object.values(message).find(
                    value => typeof value === 'string'
                ) ||
                ''
            )
        },
        label() {
            if (this.config?.label) {
                return this.config.label
            }

            return this.normalizedSeverity === 'info'
                ? ''
                : this.$t(`informationBar.${this.normalizedSeverity}`)
        },
        severityIcon() {
            if (this.normalizedSeverity === 'announcement') {
                return 'AlarmBellIcon'
            }

            if (this.normalizedSeverity === 'downtime') {
                return 'WarningIcon'
            }

            return 'InfoIcon'
        },
        isBlocking() {
            return this.normalizedSeverity === 'downtime'
        },
        pollInterval() {
            const configuredInterval = Number(this.config?.pollIntervalSeconds)

            if (!Number.isFinite(configuredInterval)) {
                return DEFAULT_POLL_INTERVAL
            }

            return Math.max(configuredInterval * 1000, MIN_POLL_INTERVAL)
        },
        informationBarUrl() {
            const baseUrl = process.env.BASE_URL || '/'
            const normalizedBaseUrl = baseUrl.endsWith('/')
                ? baseUrl
                : `${baseUrl}/`

            return `${normalizedBaseUrl}information-bar.json`
        },
    },
    watch: {
        pollInterval() {
            this.scheduleNextFetch()
        },
    },
    mounted() {
        this.loadInformationBar()
    },
    beforeDestroy() {
        window.clearTimeout(this.pollTimer)
    },
    methods: {
        async loadInformationBar() {
            try {
                const response = await window.fetch(
                    `${this.informationBarUrl}?t=${Date.now()}`,
                    {
                        cache: 'no-store',
                        headers: {
                            Accept: 'application/json',
                        },
                    }
                )

                if (!response.ok) {
                    this.config = null
                    return
                }

                this.config = await response.json()
            } catch (error) {
                this.config = null
            } finally {
                this.scheduleNextFetch()
            }
        },
        scheduleNextFetch() {
            window.clearTimeout(this.pollTimer)
            this.pollTimer = window.setTimeout(
                this.loadInformationBar,
                this.pollInterval
            )
        },
    },
}
</script>

<i18n>
{
    "en": {
        "informationBar": {
            "announcement": "Announcement",
            "downtime": "Maintenance"
        }
    },
    "de": {
        "informationBar": {
            "announcement": "Ankündigung",
            "downtime": "Wartung"
        }
    },
    "fr": {
        "informationBar": {
            "announcement": "Annonce",
            "downtime": "Maintenance"
        }
    },
    "it": {
        "informationBar": {
            "announcement": "Annuncio",
            "downtime": "Manutenzione"
        }
    }
}
</i18n>

<style lang="scss" scoped>
.information-bar {
    position: relative;
    flex-shrink: 0;
    font-size: $fs-base;
    line-height: 20px;
    text-align: center;
    z-index: 1000;

    &__bar {
        justify-content: center;
        min-height: 40px;
        padding: 8px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        &--info {
            background-color: rgba($color-pill-blue, 0.14);
            color: darken($color-pill-blue, 28%);
        }

        &--announcement {
            background-color: rgba($color-orange, 0.18);
            color: darken($color-orange, 36%);
        }

        &--downtime {
            background-color: lighten($color-danger-light, 3%);
            color: darken($color-danger, 24%);
        }
    }

    &__content {
        max-width: calc(100% - 32px);
        gap: 8px;
        justify-content: center;
    }

    &__icon {
        flex-shrink: 0;
    }

    &__label {
        flex-shrink: 0;
        font-weight: 700;
    }

    &__message {
        min-width: 0;
        overflow-wrap: anywhere;
    }

    &--blocking {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        min-height: 100dvh;
        background-color: rgba(0, 0, 0, 0.14);
        pointer-events: auto;
        z-index: 10000;

        .information-bar__bar {
            width: min(720px, calc(100% - 32px));
            min-height: 64px;
            padding: 20px 24px;
            border: 1px solid rgba($color-danger, 0.22);
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
        }

        .information-bar__content {
            max-width: 100%;
        }
    }

    @include respond-to('for-mobile-down') {
        font-size: $fs-small;

        &__bar {
            min-height: 44px;
            padding: 8px 12px;
        }

        &__content {
            max-width: 100%;
            flex-wrap: wrap;
        }
    }
}
</style>
